import React, { useEffect } from 'react';
import classNames from 'classnames';
import { MainHeader } from './main-header';
import { Avatar, Button, Col, Dropdown, Menu, Row, Space, message } from '@nackle/paper';
import { DownArrow, Gift, User } from 'app/components/shared/icons/icons';
import { LocaleString, getString } from '@nackle/intl-tools';
import { useDispatch, useSelector } from 'react-redux';
import {
	getBudgetError,
	getOriginalBudget,
	getRemainingBudget,
	getRemainingBudgetPercent,
	getSelectedBehavior,
	getSelectedProgram,
	getTotalAward,
	searchProgramsAndRewardChoices
} from 'app/app-dux/program-dux';
import { getActiveStep, setActiveStep, getErrors, clearErrors } from 'app/app-dux/app-dux';
import { getHasSelectedPeople, getSelectedPeople } from 'app/app-dux/people-dux';
import { getMessage, getSelectedEcard } from 'app/app-dux/message-dux';
import { submitRecognition, clearRecognition } from 'app/app-dux/recognition-dux';
import { APP_STORAGE_KEY, USER_STORAGE_KEY, INTEGRATION_TYPE } from '../../../../constants';

import './header.less';
import { PersonDetails } from 'app/components/shared/person-details/person-details';
import BudgetMeter from 'app/components/shared/budget-meter/budget-meter';

export function Header( props ) {
	const dispatch = useDispatch();

	const activeStep = useSelector( getActiveStep );
	const selectedPeople = useSelector( getSelectedPeople );
	const selectedProgram = useSelector( getSelectedProgram );
	const selectedBehavior = useSelector( getSelectedBehavior );
	const selectedEcard = useSelector( getSelectedEcard );
	const hasSelectedPeople = useSelector( getHasSelectedPeople );
	const totalAward = useSelector( getTotalAward );
	const originalBudget = useSelector( getOriginalBudget );
	const remainingBudget = useSelector( getRemainingBudget );
	const remainingBudgetPercent = useSelector( getRemainingBudgetPercent );
	const messageText = useSelector( getMessage );
	const errors = useSelector( getErrors );
	const budgetError = useSelector( getBudgetError );

	useEffect( () => {
		// If selected Users set header to 'people'
		if ( selectedPeople.length ) {
			handleActiveStep( 'people' );
		} else {
			// if no selected users , clear errors and recognition data and navigate to main route
			dispatch( clearErrors() );
			dispatch( clearRecognition() );
			handleActiveStep( 'main' );
			props.changeRoute( 'people', true );
		}
	}, [ hasSelectedPeople ] );

	const handleActiveStep = ( step ) => {
		dispatch( setActiveStep( step ) );
	};

	const stepForward = async () => {
		switch ( activeStep ) {
			case 'people':
				handleActiveStep( 'program' );
				await dispatch( searchProgramsAndRewardChoices() );
				props.changeRoute( 'program', true );
				break;
			case 'program':
				handleActiveStep( 'message' );
				props.changeRoute( 'message', true );
				break;
			case 'message':
				handleActiveStep( 'review' );
				props.changeRoute( 'review', true );
				break;
			case 'review':
				await handleSubmitRecognition();
				break;
			case 'success':
				await dispatch( clearRecognition() );
				handleActiveStep( 'people', true );
				break;
			default:
				handleActiveStep( 'people', true );
				break;
		}
	};

	const handleSubmitRecognition = async () => {
		const results = await dispatch( submitRecognition( INTEGRATION_TYPE, 'SUBMITTED' ) );
		if ( results.success ) {
			message.success( getString( 'recognition.success.message' ) );
			dispatch( clearRecognition() );
			handleActiveStep( '' );
		} else {
			message.error( getString( 'recognition.error.uhOh' ) );
		}
	};

	const handleEdit = ( pageName ) => {
		handleActiveStep( pageName );
		props.changeRoute( pageName );
	};

	const handleClearRecognition = () => {
		dispatch( clearErrors() );
		dispatch( clearRecognition() );
	};

	const disableButton = () => {
		const { isBehaviorRequired, pointsRequired, ecardRequired } = selectedProgram;
		let awardsComplete = true;
		selectedPeople.map( ( person ) => {
			if ( ( pointsRequired && ( !person.awardValue || person.awardValue === 0 ) ) ||
				( person.awardValue && ( person.awardValue < selectedProgram.minAward || person.awardValue > selectedProgram.maxAward ) ) )
			{
				awardsComplete = false;
				return;
			}
		} );
		return errors.length > 0 ||
			activeStep === 'program' &&
						( Object.keys( selectedProgram ).length === 0  || budgetError ||
						( isBehaviorRequired && !Object.keys( selectedBehavior ).length ) ||
						!awardsComplete  ||
						remainingBudget < 0 ) ||
			activeStep === 'people' && selectedPeople.length === 0 ||
			activeStep === 'message' &&
						( !messageText ||
							( ecardRequired && Object.keys( selectedEcard ).length === 0 ) );
	};

	const forwardButtonText = () => {
		switch ( activeStep ) {
			case 'people':
				return getString( 'recognition.button.saveRecipients' );
			case 'program':
				return getString( 'recognition.button.saveProgram' );
			case 'message':
				return getString( 'recognition.button.review' );
			case 'review':
				return getString( 'recognition.button.send' );
			default:
				return getString( 'recognition.button.saveRecipients' );
		}
	};

	const recipientsMenu = (
		<Menu>
			<Menu.Item>
				<Row justify='space-between' align='middle' className="recipients-menu">
					<Col>
						<LocaleString 
							code={ selectedPeople.length === 1 ? 'recognition.breadcrumb.totalRecipient' : 'recognition.breadcrumb.totalRecipients' }
							className="header-title"
							replaceOptions={ { recipientCount: selectedPeople.length || 0 } }
						/>
					</Col>
					<Col>
						<Button onClick={ () => handleEdit( 'people' ) }>
							<LocaleString code='recognition.button.edit' />
						</Button>
					</Col>
				</Row>
			</Menu.Item>
			{
				selectedPeople.map( ( recipient ) => {
					return (
						<Menu.Item key={ recipient.id }>
							<PersonDetails person={ recipient } flow='dropdown'/>
						</Menu.Item>
					);
				} )
			}
		</Menu>
	);

	const programMenu = (
		<Menu className="program-header-panel">
			<Menu.Item>
				<Row justify={ 'space-between' } align='middle' className="recipients-menu">
					<Col>
						<LocaleString code="recognition.breadcrumb.program" className="header-title" />
					</Col>
					<Col>
						<Button onClick={ () => handleEdit( 'program' ) }>
							<LocaleString code='recognition.button.edit' />
						</Button>
					</Col>
				</Row>
			</Menu.Item>
			{ selectedProgram.hasPoints &&
				<Menu.Item>
					<div className='budget-slider'>
						<BudgetMeter
							percent={ remainingBudgetPercent }
							current={ remainingBudget }
							totalAward={ totalAward }
							originalBudget={ originalBudget }
							additionalInfo={ selectedProgram.rewardChoiceName }
						/>
					</div>
				</Menu.Item>
			}
			{
				Object.keys( selectedBehavior ).length ?
				<Menu.Item>
					<Avatar className="behavior-image" src={ selectedBehavior.imageUrl }/>
					{ selectedBehavior.name }
				</Menu.Item> : <></>
			}
		</Menu>
	);

	return (
		<div className="header-container">
			{ activeStep === 'main' && localStorage.getItem( APP_STORAGE_KEY ) && localStorage.getItem( USER_STORAGE_KEY ) &&
				<MainHeader changeRoute={ props.changeRoute }/>
			}
			{ activeStep !== 'main' && activeStep !== 'success' &&
				<Row className="button-container spacer-lr-16 space-tb-16" justify={ activeStep === 'review' ? 'end' : 'space-between' }>
					{
						activeStep === 'people' && activeStep !== 'review' &&
							<Col className="action-buttons">
								<Button className="cancel-btn" onClick={ handleClearRecognition }> 
									<LocaleString code="recognition.button.cancel"/>
								</Button>
							</Col>
					}
					{
						activeStep !== 'people' && activeStep !== 'review' &&
						<Col className="action-buttons">
							<Space>
								<Dropdown overlay={ recipientsMenu } trigger={ [ 'click' ] }>
									<Button className="button-group">
										<User width={ 15 } height={ 14 }/>
										<span className="count">{ selectedPeople?.length ?? 0 } </span>
										<DownArrow width={ 11 } height={ 6 }/>
									</Button>
								</Dropdown>
								{ ( activeStep !== 'people' && activeStep !== 'program' ) &&
								<Dropdown overlay={ programMenu } trigger={ [ 'click' ] }>
									<Button className="button-group">
										<Gift width={ 16 } height={ 16 } className="count"/>
										<DownArrow width={ 11 } height={ 6 }/>
									</Button>
								</Dropdown>
								}
							</Space>
						</Col>
					}
					<Col className="action-buttons">
						<Button
							className={ classNames( {
								'next-button': true,
								'primary-button-disabled': disableButton(),
								'primary-button-background': !disableButton(),
							} ) }
							disabled={ disableButton() }
							onClick={ stepForward }>
							{ forwardButtonText() }
						</Button>
					</Col>
				</Row>
			}
		</div>
	);
}